import * as React from 'react';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Popover} from 'shells/popover/popover';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import {Body} from 'shells/typography/body';
import {useRefObserver} from 'shells/utils/hooks/use-ref-observer';

import type {GetContextDocuments_contextDocuments as ContextDocument} from 'nutshell-graphql-types';

import {FileUploadInputWrapper} from '../../../../core/file-upload/file-upload-input-wrapper';

import {
    useGetChatSettingsKnowledge,
    useGetContextDocuments,
    useGetContextDocumentUsage,
} from '../../graphql/hooks';

import {AddWebsiteModal} from './add-website-modal';
import {ContextDocumentTable} from './context-document-table/context-document-table';
import {EmptyState} from './empty-state';
import {getLimitExceededReasonString, sortByEffectiveStatus} from './helpers';
import {SelectedDocumentModal} from './selected-document-modal/selected-document-modal';

export function KnowledgeOptions() {
    const {chatSettings, refetch: refetchSettings} = useGetChatSettingsKnowledge();
    const [selectedDocument, setSelectedDocument] = React.useState<
        ContextDocument | null | undefined
    >(null);
    const [isAddWebsiteModalOpen, setIsAddWebsiteModalOpen] = React.useState<boolean>(false);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const fileInputRef = React.useRef<HTMLInputElement | null | undefined>(null);
    const popoverRef = React.useRef<HTMLElement | null | undefined>(null);

    const {isHovered} = useRefObserver(popoverRef, ['isHovered']);
    const {contextDocumentUsage, refetch: refetchUsage} = useGetContextDocumentUsage();
    const limitExceededReason = getLimitExceededReasonString(contextDocumentUsage);
    const hasExceededLimit = React.useMemo(() => {
        return limitExceededReason !== null;
    }, [limitExceededReason]);

    const handleRefetch = React.useCallback(() => {
        refetchUsage();
        refetchSettings();
    }, [refetchSettings, refetchUsage]);

    const {
        contextDocuments: webpages,
        isLoading: isLoadingWebpages,
        refetch: refetchWebpages,
        // @ts-expect-error
    } = useGetContextDocuments(true, 'WEB_PAGE', handleRefetch);

    const {
        contextDocuments: pdfDocuments,
        isLoading: isLoadingPdfDocuments,
        refetch: refetchPdfDocuments,
        // @ts-expect-error
    } = useGetContextDocuments(true, 'PDF', handleRefetch);

    const sortedWebpages = sortByEffectiveStatus(webpages);
    const sortedPdfDocuments = sortByEffectiveStatus(pdfDocuments);

    React.useEffect(() => {
        if (isHovered) {
            setIsPopoverOpen(true);
        } else {
            setIsPopoverOpen(false);
        }
    }, [isHovered]);

    const handleUploadSuccess = () => {
        refetchPdfDocuments();
    };

    const hasActiveWebpage = webpages.some(
        (webpage) => webpage && webpage.isIncluded && webpage.effectiveStatus === 'SUCCESS'
    );

    return (
        <>
            <FileUploadInputWrapper
                accepts={['.pdf']}
                getRef={(ref) => {
                    fileInputRef.current = ref;
                }}
                uploadUrl='/file/llama-parse-upload'
                onUploadSuccess={handleUploadSuccess}
            />
            <div className='flex flex-dir-col flex-grow pad-16'>
                <StackLegacy spacing={16}>
                    <ContextDocumentTable
                        documents={sortedWebpages}
                        isLoading={isLoadingWebpages}
                        onRefetch={refetchWebpages}
                        title='Webpages'
                        firstRow={
                            <AddButtonRow
                                buttonText='Add new website'
                                onClick={() => setIsAddWebsiteModalOpen(true)}
                            />
                        }
                        emptyState={<EmptyState sourceType='WEB_PAGE' />}
                        primaryColumn={{
                            id: 'url',
                            header: 'URL',
                        }}
                        onSelectDocument={(document) => setSelectedDocument(document)}
                        canRecrawl={hasActiveWebpage}
                        canToggle={!hasExceededLimit}
                    />
                    <ContextDocumentTable
                        documents={sortedPdfDocuments}
                        isLoading={isLoadingPdfDocuments}
                        onRefetch={refetchPdfDocuments}
                        title='Documents'
                        firstRow={
                            <AddButtonRow
                                buttonText='Upload new document'
                                onClick={() => {
                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                    fileInputRef.current && fileInputRef.current.click();
                                }}
                                isDisabled={hasExceededLimit}
                                getRef={(ref) => {
                                    popoverRef.current = ref;
                                }}
                            />
                        }
                        emptyState={<EmptyState sourceType='PDF' />}
                        primaryColumn={{
                            id: 'name',
                            header: 'Document Title',
                        }}
                        onSelectDocument={(document) => setSelectedDocument(document)}
                        canToggle={!hasExceededLimit}
                    />
                    {(NutClientConfig.isDevelopmentOrPublisher ||
                        NutClientConfig.isSupportLogin) && (
                        <div className='full-width text-align-center'>
                            <Link size='big' href='/support/inbox' variant='button-primary'>
                                View hidden web pages (DEV AND SUPPORT ONLY)
                            </Link>
                        </div>
                    )}
                </StackLegacy>
            </div>
            {selectedDocument && (
                <SelectedDocumentModal
                    documentId={selectedDocument.id}
                    sourceType={selectedDocument.sourceType}
                    onClose={() => setSelectedDocument(null)}
                    canToggle={!hasExceededLimit}
                    chatSettings={chatSettings}
                />
            )}
            {isAddWebsiteModalOpen && (
                <AddWebsiteModal
                    onClose={() => setIsAddWebsiteModalOpen(false)}
                    refetchContextDocuments={refetchWebpages}
                    limitExceededReason={limitExceededReason}
                    hasExceededLimit={hasExceededLimit}
                />
            )}
            {hasExceededLimit && isPopoverOpen && (
                <Popover anchor={popoverRef.current} location='top' bodyColor='error'>
                    <div className='pad-16'>
                        <Body size='small' color='white'>
                            {limitExceededReason}
                        </Body>
                    </div>
                </Popover>
            )}
        </>
    );
}
